/**
 * PATH: apps/ai/src/pages/SkillsPage.tsx
 * PURPOSE: Orchestrator — AI Skills showcase with auth and loading states
 */

import { useState, useEffect, useCallback } from 'react';
import { Sparkles, Zap, ExternalLink } from 'lucide-react';
import { useAuthStore } from '@quotech/auth';
import { AI_SKILLS, type AISkill } from '../services/aiSkills';
import {
  SkillsGridSkeleton, ErrorState, EmptyState, AuthRequiredState,
  SkillCard, FollowupSuggestionsDemo,
} from './skills/SkillCard';

type LoadingState = 'idle' | 'loading' | 'success' | 'error';
interface PageState { skillsLoading: LoadingState; skillsError: string | null; authChecked: boolean; isAuthenticated: boolean; }

export default function SkillsPage() {
  const [selectedSkill, setSelectedSkill] = useState<AISkill | null>(null);
  const [pageState, setPageState] = useState<PageState>({ skillsLoading: 'loading', skillsError: null, authChecked: false, isAuthenticated: false });
  const { isAuthenticated, checkAuth } = useAuthStore();

  useEffect(() => {
    const init = async () => {
      try {
        await checkAuth();
        await new Promise(resolve => setTimeout(resolve, 300));
        setPageState({ skillsLoading: 'success', skillsError: null, authChecked: true, isAuthenticated });
      } catch (err: unknown) {
        setPageState(prev => ({ ...prev, skillsLoading: 'error', skillsError: (err as Error).message || 'Failed to initialize', authChecked: true, isAuthenticated: false }));
      }
    };
    init();
  }, [checkAuth, isAuthenticated]);

  const handleRetry = useCallback(() => { setPageState(prev => ({ ...prev, skillsLoading: 'loading', skillsError: null })); window.location.reload(); }, []);
  const handleTrySkill = useCallback((skill: AISkill) => { if (skill.id !== 'followup-suggestions') setSelectedSkill(skill); }, []);

  const renderContent = () => {
    if (!pageState.authChecked || pageState.skillsLoading === 'loading') return <SkillsGridSkeleton />;
    if (pageState.authChecked && !isAuthenticated) return <AuthRequiredState />;
    if (pageState.skillsLoading === 'error' && pageState.skillsError) return <ErrorState message={pageState.skillsError} onRetry={handleRetry} type={pageState.skillsError.includes('network') ? 'network' : 'generic'} />;
    if (AI_SKILLS.length === 0) return <EmptyState />;
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{AI_SKILLS.map((skill) => <SkillCard key={skill.id} skill={skill} onTry={handleTrySkill} />)}</div>;
  };

  const isReady = pageState.authChecked && isAuthenticated && pageState.skillsLoading === 'success';

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/80"><Sparkles className="w-6 h-6 text-primary-foreground" /></div>
            <div><h1 className="text-2xl font-bold text-foreground">AI Skills</h1><p className="text-muted-foreground">Specialized AI capabilities for Quotech</p></div>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">These AI skills are designed to help you work smarter across quotes, jobs, and customer communication.</p>
        </div>
        <div className="mb-8">{renderContent()}</div>
        {isReady && <div className="mb-8"><FollowupSuggestionsDemo /></div>}
        {isReady && (
          <div className="p-6 rounded-xl border border-dashed border-border bg-muted/20">
            <div className="flex items-center gap-3 mb-4"><Zap className="w-5 h-5 text-amber-500" /><h3 className="text-lg font-semibold text-foreground">More Skills Coming Soon</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[{ name: 'Contract Analysis', description: 'Extract key terms and obligations' }, { name: 'Cost Prediction', description: 'ML-powered cost estimation' }, { name: 'Schedule Optimization', description: 'AI workforce scheduling' }].map((s) => <div key={s.name} className="p-4 rounded-lg bg-muted/30"><h4 className="font-medium text-foreground mb-1">{s.name}</h4><p className="text-sm text-muted-foreground">{s.description}</p></div>)}
            </div>
          </div>
        )}
        {selectedSkill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && setSelectedSkill(null)}>
            <div className="relative w-full max-w-md bg-card rounded-xl shadow-2xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">{selectedSkill.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{selectedSkill.description}</p>
              {selectedSkill.requiresQuoteId && <p className="text-sm text-amber-500 mb-4">This skill requires a Quote ID. Use it from the Quote Hub.</p>}
              {selectedSkill.requiresJobId && <p className="text-sm text-amber-500 mb-4">This skill requires a Job ID. Use it from the Jobs Hub.</p>}
              <div className="flex items-center justify-end gap-3">
                <button onClick={() => setSelectedSkill(null)} className="px-4 py-2 text-muted-foreground hover:bg-muted/50 rounded-lg transition-colors">Close</button>
                <a href={selectedSkill.requiresJobId ? 'https://dashboard.quotech.ai' : 'https://quote.quotech.ai'} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"><ExternalLink className="w-4 h-4" />Open in {selectedSkill.requiresJobId ? 'Jobs Hub' : 'Quote Hub'}</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
